import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'link';
type Size = 'sm' | 'md' | 'lg';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  /** Volle Breite — z.B. für Form-Submits auf Mobile */
  fullWidth?: boolean;
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: cn(
    'bg-gold-2 text-paper border border-gold-2',
    'hover:bg-ink-2 hover:border-ink-2 hover:-translate-y-0.5',
    'active:translate-y-0',
    'shadow-sm hover:shadow-md',
  ),
  secondary: cn(
    'bg-transparent text-ink-2 border border-gold-2',
    'hover:bg-gold/10 hover:border-gold',
    'active:bg-gold/20',
  ),
  ghost: cn(
    'bg-transparent text-ink-2 border border-transparent',
    'hover:text-gold-2 hover:underline underline-offset-4',
  ),
  destructive: cn(
    'bg-it-red text-paper border border-it-red',
    'hover:bg-it-red/90 hover:-translate-y-0.5',
    'active:translate-y-0',
  ),
  link: cn(
    'bg-transparent text-gold border-0 underline underline-offset-4',
    'hover:text-gold-2',
    // min-h-11 statt h-auto: die link-Variante umgeht SIZE_CLASSES komplett
    // (siehe isLink weiter unten) und hatte dadurch gar keine definierte
    // Trefferfläche. inline-flex zentriert den Text in der größeren Fläche.
    'p-0 min-h-11 inline-flex items-center',
  ),
};

const SIZE_CLASSES: Record<Size, string> = {
  // sm war h-9 (36px) und damit unter dem 44px-Ziel — bei einer zentralen
  // Button-Komponente schlägt das auf jede Verwendung durch.
  sm: 'h-11 px-3 text-xs tracking-[0.22em] gap-1.5',
  md: 'h-11 px-5 text-xs tracking-[0.22em] gap-2',
  lg: 'h-14 px-7 text-sm tracking-[0.22em] gap-2.5',
};

/**
 * Sizilianer-Button.
 *
 * Varianten:
 * - `primary`: Gold-gefüllt, Haupt-Action
 * - `secondary`: Border + transparent, sekundäre Action
 * - `ghost`: nur Text, dezent
 * - `destructive`: Rot, irreversible Aktionen
 * - `link`: text-only mit Underline
 *
 * @example
 *   <Button variant="primary" iconRight={<ArrowRight />}>
 *     Reservieren
 *   </Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      iconLeft,
      iconRight,
      fullWidth = false,
      className,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) {
    const isDisabled = disabled || loading;
    const isLink = variant === 'link';

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={cn(
          'inline-flex items-center justify-center',
          'font-sans font-semibold uppercase',
          'cursor-pointer select-none',
          'transition-[color,background-color,border-color,transform,box-shadow] duration-200',
          'focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2',
          isDisabled && 'opacity-50 cursor-not-allowed pointer-events-none',
          fullWidth && 'w-full',
          VARIANT_CLASSES[variant],
          !isLink && SIZE_CLASSES[size],
          className,
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        ) : (
          iconLeft && <span className="shrink-0">{iconLeft}</span>
        )}
        {children}
        {!loading && iconRight && <span className="shrink-0">{iconRight}</span>}
      </button>
    );
  },
);

type ButtonLinkProps = LinkProps & {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
};

/**
 * Link im Button-Gewand — für Navigations-CTAs.
 *
 * WCAG 4.1.2: Ein `<button>` in einem `<a>` ist ungültiges HTML mit zwei
 * verschachtelten fokussierbaren Controls (doppelter Tab-Stopp, widersprüchliche
 * Rollen). Für Routen-CTAs daher IMMER ButtonLink statt <Link><Button/></Link>.
 *
 * @example
 *   <ButtonLink to="/reservieren" size="lg" iconRight={<ArrowRight />}>
 *     Tisch reservieren
 *   </ButtonLink>
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  function ButtonLink(
    {
      variant = 'primary',
      size = 'md',
      iconLeft,
      iconRight,
      fullWidth = false,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const isLink = variant === 'link';
    return (
      <Link
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center',
          'font-sans font-semibold uppercase',
          'cursor-pointer select-none no-underline',
          'transition-[color,background-color,border-color,transform,box-shadow] duration-200',
          'focus-visible:outline-2 focus-visible:outline-gold focus-visible:outline-offset-2',
          fullWidth && 'w-full',
          VARIANT_CLASSES[variant],
          !isLink && SIZE_CLASSES[size],
          className,
        )}
        {...props}
      >
        {iconLeft && <span className="shrink-0">{iconLeft}</span>}
        {children}
        {iconRight && <span className="shrink-0">{iconRight}</span>}
      </Link>
    );
  },
);
